import React, { useState } from 'react';
import { controllers } from '../../Controllers/Controllers';
import { LogoView } from '../../Components/Views/LogoView';
import '../../assets/styles/authpage.css'

const AuthModal = ({hideModal}) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [generalError, setGeneralError] = useState('');

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear field-specific error when user starts typing
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: ''
      });
    }
  };

  const clearErrors = () => {
    setErrors({});
    setGeneralError('');
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    clearErrors();

    try {
      const result = await controllers().auth.signIn(formData);
      if(result.statusCode!==200){
        return handleAuthError(result.error);
      }
      hideModal();
    } catch (error) {
      handleAuthError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAuthError = (error) => {
    // Handle different types of errors from the server
    if (error.response?.data) {
      const errorData = error.response.data;

      // Handle field-specific errors
      if (errorData.errors || errorData.field_errors) {
        const fieldErrors = errorData.errors || errorData.field_errors;
        const newErrors = {};

        Object.keys(fieldErrors).forEach(field => {
          if (Array.isArray(fieldErrors[field])) {
            newErrors[field] = fieldErrors[field][0]; // Take first error message
          } else {
            newErrors[field] = fieldErrors[field];
          }
        });

        setErrors(newErrors);
      }

      // Handle general error messages
      if (errorData.message || errorData.error || errorData.detail) {
        setGeneralError(errorData.message || errorData.error || errorData.detail);
      } else if (errorData.non_field_errors) {
        setGeneralError(Array.isArray(errorData.non_field_errors)
          ? errorData.non_field_errors[0]
          : errorData.non_field_errors);
      }
    } else {
      // Handle network errors or other issues
      setGeneralError(getErrorMessage(error));
    }
  };

  const getErrorMessage = (error) => {
    if (error.message) {
      return error.message;
    }
    if(error){
      return error
    }
    if (error.code) {
      return `Authentication failed (${error.code})`;
    }

    return 'An unexpected error occurred. Please try again';
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container signin-page">
        {isLoading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
          </div>
        )}

        <form className="auth-content" onSubmit={handleEmailAuth}>
          <div className="auth-logo">
            <LogoView />
          </div>
          <h2 className="auth-title">Sign In</h2>
          <p className="auth-subtitle">Enter your credentials to sign in</p>

          {generalError && (
            <div className="error-banner">
              <span className="error-text">{generalError}</span>
              <button
                type="button"
                className="error-close"
                onClick={() => setGeneralError('')}
              >
                ×
              </button>
            </div>
          )}

          <div className="form-container">
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`form-input ${errors.email ? 'error' : ''}`}
                placeholder="Enter your email"
                disabled={isLoading}
              />
              {errors.email && <div className="field-error">{errors.email}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className={`form-input ${errors.password ? 'error' : ''}`}
                placeholder="Enter your password"
                disabled={isLoading}
              />
              {errors.password && <div className="field-error">{errors.password}</div>}
            </div>

            <button
              type="submit"
              className="submit-btn"
              disabled={isLoading}
            >
              {isLoading ? 'Signing In...' : 'Sign In'}
            </button>
          </div>

          <div className="forgot-password">
            <button type="button" className="forgot-btn" disabled={isLoading}>
              Forgot your password?
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AuthModal;
